import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { safeFormatDate, toIsoLocal } from '../lib/dateTime';
import {
  Settings,
  Shield,
  Users,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Tag,
  Clock,
  CalendarDays,
  AlertTriangle,
  Info,
  Eye,
  FileText,
  Upload,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PayrollRulesTab from '../components/PayrollRulesTab';
import { CountrySelect, countryName } from '../components/EInvoiceFields';
import BankAccountsPanel from '../components/BankAccountsPanel';

export default function SettingsPage() {
  const { api, user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [userAttributes, setUserAttributes] = useState([]);
  const [wageTypes, setWageTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Holiday Settings State
  const [holidaySettings, setHolidaySettings] = useState(null);
  const [states, setStates] = useState([]);
  const [holidayForm, setHolidayForm] = useState({
    default_state: 'NI',
    holiday_calculation_start_date: '',
  });
  const [isHolidaySaving, setIsHolidaySaving] = useState(false);
  const [showHolidayWarning, setShowHolidayWarning] = useState(false);
  
  // Utilization visibility setting
  const [employeesSeePct, setEmployeesSeePct] = useState(false);
  const [isSavingPctSetting, setIsSavingPctSetting] = useState(false);

  // Pausenzeiten-Pflicht (§ 4 ArbZG)
  const [requireBreakTimes, setRequireBreakTimes] = useState(false);
  const [isSavingBreakTimes, setIsSavingBreakTimes] = useState(false);
  useEffect(() => {
    api.get('/company-settings/break-times')
      .then((res) => setRequireBreakTimes(res.data?.require_break_times ?? false))
      .catch(() => {});
  }, [api]);  
  
  // Team Overview Settings
  const [supervisorScope, setSupervisorScope] = useState('team');
  const [accountingSeesTeamOverview, setAccountingSeesTeamOverview] = useState(false);
  const [isSavingTeamOverviewSettings, setIsSavingTeamOverviewSettings] = useState(false);
  
  // Billing Adjustment Settings
  const [accountingMayAdjustBilling, setAccountingMayAdjustBilling] = useState(false);
  const [isSavingBillingAdjustment, setIsSavingBillingAdjustment] = useState(false);
  
  // Invoice Tab State - Company Profile
  const [companyProfile, setCompanyProfile] = useState({});
  const [companyProfileForm, setCompanyProfileForm] = useState({});
  const [companyProfileChanged, setCompanyProfileChanged] = useState(false);
  const [isSavingCompanyProfile, setIsSavingCompanyProfile] = useState(false);
  
  // Invoice Tab State - Invoice Defaults
  const [invoiceDefaults, setInvoiceDefaults] = useState({});
  const [invoiceDefaultsForm, setInvoiceDefaultsForm] = useState({});
  const [invoiceDefaultsChanged, setInvoiceDefaultsChanged] = useState(false);
  const [isSavingInvoiceDefaults, setIsSavingInvoiceDefaults] = useState(false);
  
  // Invoice Tab State - Invoice Numbering
  const [invoiceNumbering, setInvoiceNumbering] = useState({});
  const [invoiceNumberingForm, setInvoiceNumberingForm] = useState({});
  const [invoiceNumberingChanged, setInvoiceNumberingChanged] = useState(false);
  const [isSavingInvoiceNumbering, setIsSavingInvoiceNumbering] = useState(false);
  
  // Live Mode Confirmation Dialog
  const [showLiveModeDialog, setShowLiveModeDialog] = useState(false);
  const [liveModeConfirmText, setLiveModeConfirmText] = useState('');
  
  // Billing Permissions State
  const defaultBillingPermissions = {
    create: ['accounting'],
    edit: ['accounting'],
    approve: ['supervisor'],
    issue: ['accounting'],
    send: ['accounting'],
    void: ['accounting'],
    delete: ['accounting'],
  };
  const [billingPermissions, setBillingPermissions] = useState(defaultBillingPermissions);
  const [billingPermissionsForm, setBillingPermissionsForm] = useState(defaultBillingPermissions);
  const [billingPermissionsChanged, setBillingPermissionsChanged] = useState(false);
  const [isSavingBillingPermissions, setIsSavingBillingPermissions] = useState(false);
  
  // Auto-closed sessions State (Admin Stempeluhr Tab)
  const [autoClosedSessions, setAutoClosedSessions] = useState([]);
  const [autoClosedLoading, setAutoClosedLoading] = useState(false);
  
  // Dialog states
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [isAttrDialogOpen, setIsAttrDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  
  const [roleForm, setRoleForm] = useState({
    name: '',
    display_name: '',
    description: '',
    permissions: [],
  });
  
  const [attrForm, setAttrForm] = useState({
    name: '',
    display_name: '',
    field_type: 'text',
    required: false,
    options: '',
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes, attrsRes, wageRes, statesRes, holidaySettingsRes, pctSettingRes, teamOverviewSettingsRes, billingAdjustmentRes, companyProfileRes, invoiceDefaultsRes, invoiceNumberingRes, billingPermissionsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions'),
        api.get('/user-attributes'),
        api.get('/datev/wage-types'),
        api.get('/holidays/states'),
        api.get('/holidays/settings').catch(() => ({ data: null })),
        api.get('/company-settings/utilization-visibility').catch(() => ({ data: { employees_see_utilization_pct: false } })),
        api.get('/company-settings/team-overview').catch(() => ({ data: { supervisor_scope: 'team', accounting_sees_team_overview: false } })),
        api.get('/company-settings/billing-adjustment').catch(() => ({ data: { accounting_may_adjust_billing_quantities: false } })),
        api.get('/company-settings/company-profile').catch(() => ({ data: {} })),
        api.get('/company-settings/invoice-defaults').catch(() => ({ data: {} })),
        api.get('/company-settings/invoice-numbering').catch(() => ({ data: {} })),
        api.get('/company-settings/billing-permissions').catch(() => ({ data: defaultBillingPermissions })),
      ]);
      // Combine builtin and custom roles into a single array
      const rolesData = rolesRes.data;
      const allRoles = [
        ...(rolesData.builtin || []).map(r => ({
          name: r.value,
          display_name: r.label,
          description: r.description,
          is_default: true,
          permissions: []
        })),
        ...(rolesData.custom || [])
      ];
      setRoles(allRoles);
      setPermissions(permsRes.data);
      setUserAttributes(attrsRes.data);
      // Transform wage types from object to array
      const wageTypesData = wageRes.data;
      const wageTypesArray = Object.entries(wageTypesData).map(([code, name]) => ({
        code,
        name
      }));
      setWageTypes(wageTypesArray);
      
      // Holiday settings
      setStates(statesRes.data);
      if (holidaySettingsRes.data) {
        setHolidaySettings(holidaySettingsRes.data);
        setHolidayForm({
          default_state: holidaySettingsRes.data.default_state || 'NI',
          holiday_calculation_start_date: holidaySettingsRes.data.holiday_calculation_start_date || '',
        });
      }
      
      // Utilization visibility setting
      setEmployeesSeePct(pctSettingRes.data?.employees_see_utilization_pct ?? false);
      
      // Team Overview settings
      setSupervisorScope(teamOverviewSettingsRes.data?.supervisor_scope ?? 'team');
      setAccountingSeesTeamOverview(teamOverviewSettingsRes.data?.accounting_sees_team_overview ?? false);
      
      // Billing adjustment settings
      setAccountingMayAdjustBilling(billingAdjustmentRes.data?.accounting_may_adjust_billing_quantities ?? false);
      
      // Invoice Tab - Company Profile
      const profileData = companyProfileRes.data || {};
      setCompanyProfile(profileData);
      setCompanyProfileForm({ ...profileData });
      
      // Invoice Tab - Invoice Defaults
      const defaultsData = invoiceDefaultsRes.data || {};
      setInvoiceDefaults(defaultsData);
      setInvoiceDefaultsForm({ ...defaultsData });
      
      // Invoice Tab - Invoice Numbering
      const numberingData = invoiceNumberingRes.data || {};
      setInvoiceNumbering(numberingData);
      setInvoiceNumberingForm({ ...numberingData });
      
      // Invoice Tab - Billing Permissions
      const permissionsData = billingPermissionsRes.data || defaultBillingPermissions;
      setBillingPermissions(permissionsData);
      setBillingPermissionsForm({ ...permissionsData });
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUtilizationVisibilityChange = async (checked) => {
    setIsSavingPctSetting(true);
    try {
      await api.put('/company-settings/utilization-visibility', {
        employees_see_utilization_pct: checked
      });
      setEmployeesSeePct(checked);
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Failed to save visibility setting:', error);
      toast.error('Fehler beim Speichern der Einstellung');
    } finally {
      setIsSavingPctSetting(false);
    }
  };

  const handleBreakTimesChange = async (checked) => {
    setIsSavingBreakTimes(true);
    try {
      await api.put('/company-settings/break-times', { require_break_times: checked });
      setRequireBreakTimes(checked);
      toast.success(checked ? 'Pausenzeiten sind jetzt Pflicht' : 'Pausenzeiten-Pflicht ausgeschaltet');
    } catch (error) {
      toast.error('Fehler beim Speichern der Einstellung');
    } finally {
      setIsSavingBreakTimes(false);
    }
  };  

  const handleTeamOverviewSettingsChange = async (newSupervisorScope, newAccountingSeesTeamOverview) => {
    const prevSupervisorScope = supervisorScope;
    const prevAccountingSeesTeamOverview = accountingSeesTeamOverview;
    
    setIsSavingTeamOverviewSettings(true);
    setSupervisorScope(newSupervisorScope);
    setAccountingSeesTeamOverview(newAccountingSeesTeamOverview);
    
    try {
      await api.put('/company-settings/team-overview', {
        supervisor_scope: newSupervisorScope,
        accounting_sees_team_overview: newAccountingSeesTeamOverview
      });
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Failed to save team overview setting:', error);
      toast.error('Fehler beim Speichern der Einstellung');
      // Restore previous values
      setSupervisorScope(prevSupervisorScope);
      setAccountingSeesTeamOverview(prevAccountingSeesTeamOverview);
    } finally {
      setIsSavingTeamOverviewSettings(false);
    }
  };

  const handleBillingAdjustmentChange = async (checked) => {
    const prevValue = accountingMayAdjustBilling;
    setAccountingMayAdjustBilling(checked);
    setIsSavingBillingAdjustment(true);
    try {
      await api.put('/company-settings/billing-adjustment', {
        accounting_may_adjust_billing_quantities: checked
      });
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Failed to save billing adjustment setting:', error);
      toast.error('Fehler beim Speichern der Einstellung');
      // Restore previous value
      setAccountingMayAdjustBilling(prevValue);
    } finally {
      setIsSavingBillingAdjustment(false);
    }
  };

  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        display_name: role.display_name,
        description: role.description || '',
        permissions: role.permissions || [],
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        name: '',
        display_name: '',
        description: '',
        permissions: [],
      });
    }
    setIsRoleDialogOpen(true);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.name}`, roleForm);
        toast.success('Rolle aktualisiert');
      } else {
        await api.post('/roles', roleForm);
        toast.success('Rolle erstellt');
      }
      setIsRoleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteRole = async (roleName) => {
    if (!window.confirm('Möchten Sie diese Rolle wirklich löschen?')) return;
    try {
      await api.delete(`/roles/${roleName}`);
      toast.success('Rolle gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const togglePermission = (permName) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permName)
        ? prev.permissions.filter(p => p !== permName)
        : [...prev.permissions, permName]
    }));
  };

  const openAttrDialog = () => {
    setAttrForm({
      name: '',
      display_name: '',
      field_type: 'text',
      required: false,
      options: '',
      description: '',
    });
    setIsAttrDialogOpen(true);
  };

  const handleAttrSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...attrForm,
        options: attrForm.field_type === 'select' && attrForm.options
          ? attrForm.options.split(',').map(o => o.trim())
          : null,
      };
      await api.post('/user-attributes', data);
      toast.success('Attribut erstellt');
      setIsAttrDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleDeleteAttr = async (attrName) => {
    if (!window.confirm('Möchten Sie dieses Attribut wirklich löschen?')) return;
    try {
      await api.delete(`/user-attributes/${attrName}`);
      toast.success('Attribut gelöscht');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // Holiday Settings functions
  const handleHolidayFormChange = (field, value) => {
    setHolidayForm(prev => ({ ...prev, [field]: value }));
    
    // Check if date is in the past for warning
    if (field === 'holiday_calculation_start_date' && value) {
      const today = toIsoLocal(new Date());
      setShowHolidayWarning(value < today);
    }
  };

  const handleHolidaySettingsSave = async () => {
    setIsHolidaySaving(true);
    try {
      const response = await api.put('/holidays/settings', {
        default_state: holidayForm.default_state,
        holiday_calculation_start_date: holidayForm.holiday_calculation_start_date || null,
      });
      setHolidaySettings(response.data);
      toast.success('Feiertagseinstellungen gespeichert');
      setShowHolidayWarning(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setIsHolidaySaving(false);
    }
  };

  const getStateName = (code) => {
    const state = states.find(s => s.code === code);
    return state?.name || code;
  };

  // ===== Invoice Tab Handlers =====
  
  // ===== Auto-closed Sessions (Stempeluhr Tab) =====
  const fetchAutoClosedSessions = async () => {
    if (user?.role !== 'admin') return;
    setAutoClosedLoading(true);
    try {
      const res = await api.get('/clock/auto-closed');
      setAutoClosedSessions(res.data);
    } catch (error) {
      console.error('Failed to fetch auto-closed sessions:', error);
      toast.error('Fehler beim Laden der automatisch beendeten Sitzungen');
    } finally {
      setAutoClosedLoading(false);
    }
  };
  
  // Company Profile handlers
  const handleCompanyProfileChange = (field, value) => {
    setCompanyProfileForm(prev => ({ ...prev, [field]: value }));
    setCompanyProfileChanged(true);
  };
  
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Das Logo darf höchstens 2 MB groß sein.');
      e.target.value = '';
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      handleCompanyProfileChange('logo_base64', event.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveLogo = () => {
    handleCompanyProfileChange('logo_base64', null);
  };
  
  const handleSaveCompanyProfile = async () => {
    setIsSavingCompanyProfile(true);
    try {
      const response = await api.put('/company-settings/company-profile', companyProfileForm);
      setCompanyProfile(response.data);
      setCompanyProfileForm({ ...response.data });
      setCompanyProfileChanged(false);
      toast.success('Firmenstammdaten gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Firmenstammdaten');
    } finally {
      setIsSavingCompanyProfile(false);
    }
  };
  
  // Invoice Defaults handlers
  const handleInvoiceDefaultsChange = (field, value) => {
    setInvoiceDefaultsForm(prev => ({ ...prev, [field]: value }));
    setInvoiceDefaultsChanged(true);
  };
  
  const handleSaveInvoiceDefaults = async () => {
    setIsSavingInvoiceDefaults(true);
    try {
      const response = await api.put('/company-settings/invoice-defaults', invoiceDefaultsForm);
      setInvoiceDefaults(response.data);
      setInvoiceDefaultsForm({ ...response.data });
      setInvoiceDefaultsChanged(false);
      toast.success('Rechnungsoptionen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Rechnungsoptionen');
    } finally {
      setIsSavingInvoiceDefaults(false);
    }
  };
  
  // Invoice Numbering handlers
  const handleInvoiceNumberingChange = (field, value) => {
    setInvoiceNumberingForm(prev => ({ ...prev, [field]: value }));
    setInvoiceNumberingChanged(true);
  };
  
  const handleSaveInvoiceNumbering = async () => {
    setIsSavingInvoiceNumbering(true);
    try {
      const response = await api.put('/company-settings/invoice-numbering', invoiceNumberingForm);
      setInvoiceNumbering(response.data);
      setInvoiceNumberingForm({ ...response.data });
      setInvoiceNumberingChanged(false);
      toast.success('Rechnungsnummern-Einstellungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Rechnungsnummern-Einstellungen');
    } finally {
      setIsSavingInvoiceNumbering(false);
    }
  };
  
  // Format preview for invoice number
  const formatInvoiceNumberPreview = () => {
    const format = invoiceNumberingForm.format || '{seq}/{year}';
    const prefix = invoiceNumberingForm.prefix || '';
    const seqPadding = invoiceNumberingForm.seq_padding || 0;
    const nextSeq = invoiceNumberingForm.next_seq || 1;
    const year = new Date().getFullYear();
    
    let seqStr = String(nextSeq);
    if (seqPadding > 0) {
      seqStr = seqStr.padStart(seqPadding, '0');
    }
    
    return format
      .replace('{seq}', seqStr)
      .replace('{year}', String(year))
      .replace('{yy}', String(year).slice(-2))
      .replace('{prefix}', prefix);
  };
  
  // Live mode activation
  const handleStartLiveMode = async () => {
    setIsSavingInvoiceNumbering(true);
    try {
      const payload = {
        ...invoiceNumberingForm,
        live_mode: true,
      };
      const response = await api.put('/company-settings/invoice-numbering', payload);
      setInvoiceNumbering(response.data);
      setInvoiceNumberingForm({ ...response.data });
      setInvoiceNumberingChanged(false);
      setShowLiveModeDialog(false);
      setLiveModeConfirmText('');
      toast.success('Livebetrieb aktiviert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktivieren des Livebetriebs');
    } finally {
      setIsSavingInvoiceNumbering(false);
    }
  };
  
  // Check if next_seq can be saved in live mode (only increase allowed)
  const canSaveNextSeqInLiveMode = () => {
    if (!invoiceNumbering.live_mode) return true;
    const formValue = parseInt(invoiceNumberingForm.next_seq) || 1;
    const savedValue = parseInt(invoiceNumbering.next_seq) || 1;
    return formValue >= savedValue;
  };
  
  // Billing Permissions handlers
  const handleBillingPermissionToggle = (action, role) => {
    setBillingPermissionsForm(prev => {
      const current = prev[action] || [];
      const updated = current.includes(role)
        ? current.filter(r => r !== role)
        : [...current, role];
      return { ...prev, [action]: updated };
    });
    setBillingPermissionsChanged(true);
  };
  
  const handleSaveBillingPermissions = async () => {
    setIsSavingBillingPermissions(true);
    try {
      const response = await api.put('/company-settings/billing-permissions', billingPermissionsForm);
      setBillingPermissions(response.data);
      setBillingPermissionsForm({ ...response.data });
      setBillingPermissionsChanged(false);
      toast.success('Berechtigungen gespeichert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern der Berechtigungen');
      // Rollback to loaded state
      setBillingPermissionsForm({ ...billingPermissions });
      setBillingPermissionsChanged(false);
    } finally {
      setIsSavingBillingPermissions(false);
    }
  };
  
  // Check if approve and issue have exactly the same roles (four-eyes warning)
  const hasFourEyesWarning = () => {
    const approveRoles = [...(billingPermissionsForm.approve || [])].sort();
    const issueRoles = [...(billingPermissionsForm.issue || [])].sort();
    if (approveRoles.length === 0 && issueRoles.length === 0) return false;
    return JSON.stringify(approveRoles) === JSON.stringify(issueRoles);
  };

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-16">
        <Shield className="w-12 h-12 text-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Kein Zugriff</h3>
        <p className="text-muted-foreground">Diese Seite ist nur für Administratoren zugänglich.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Einstellungen</h1>
        <p className="text-muted-foreground mt-1">Rollen, Berechtigungen und Benutzerattribute verwalten</p>
      </div>

      <Tabs defaultValue="roles" className="w-full" onValueChange={(v) => { if (v === 'clock') fetchAutoClosedSessions(); }}>
        <TabsList className="bg-card/50 border border-border flex-wrap">
          <TabsTrigger value="roles" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Shield className="w-4 h-4 mr-2" />
            Rollen
          </TabsTrigger>
          <TabsTrigger value="holidays" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <CalendarDays className="w-4 h-4 mr-2" />
            Feiertage
          </TabsTrigger>
          <TabsTrigger value="payroll" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Clock className="w-4 h-4 mr-2" />
            Arbeitszeitregeln
          </TabsTrigger>
          <TabsTrigger value="attributes" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Tag className="w-4 h-4 mr-2" />
            Benutzerfelder
          </TabsTrigger>
          <TabsTrigger value="datev" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Settings className="w-4 h-4 mr-2" />
            DATEV
          </TabsTrigger>
          <TabsTrigger value="invoice" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <FileText className="w-4 h-4 mr-2" />
            Rechnung
          </TabsTrigger>
          <TabsTrigger value="display" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Eye className="w-4 h-4 mr-2" />
            Anzeige
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="clock" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
              <Clock className="w-4 h-4 mr-2" />
              Stempeluhr
            </TabsTrigger>
          )}
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Rollen und deren Berechtigungen verwalten</p>
            <Button onClick={() => openRoleDialog()} className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
              <Plus className="w-4 h-4 mr-2" />
              Neue Rolle
            </Button>
          </div>

          <div className="grid gap-4">
            {roles.map((role) => (
              <motion.div
                key={role.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-glass p-5"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-medium text-foreground">{role.display_name}</h3>
                      <span className="px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground">{role.name}</span>
                      {role.is_default && (
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-500/20 text-indigo-400">Standard</span>
                      )}
                    </div>
                    {role.description && (
                      <p className="text-sm text-muted-foreground mt-1">{role.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {role.permissions?.map((perm) => (
                        <span key={perm} className="px-2 py-1 text-xs rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                          {permissions.find(p => p.name === perm)?.display_name || perm}
                        </span>
                      ))}
                    </div>
                  </div>
                  {!role.is_default && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => openRoleDialog(role)} className="border-border">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDeleteRole(role.name)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" className="mt-6 space-y-4">
          <p className="text-muted-foreground">Unternehmensweite Feiertagseinstellungen</p>
          
          <div className="card-glass p-6 space-y-6">
            {/* Company State */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Bundesland des Unternehmens</Label>
              <Select
                value={holidayForm.default_state}
                onValueChange={(v) => handleHolidayFormChange('default_state', v)}
              >
                <SelectTrigger className="w-full max-w-xs bg-input border-border text-foreground">
                  <SelectValue placeholder="Bundesland wählen" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[300px]">
                  {states.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Dieses Bundesland wird für Mitarbeiter ohne eigene Einstellung verwendet.
              </p>
            </div>

            {/* Calculation Start Date */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Feiertagsberechnung gültig ab</Label>
              <Input
                type="date"
                value={holidayForm.holiday_calculation_start_date}
                onChange={(e) => handleHolidayFormChange('holiday_calculation_start_date', e.target.value)}
                className="w-full max-w-xs bg-input border-border text-foreground"
              />
              <p className="text-sm text-muted-foreground">
                Ab diesem Datum werden landesspezifische Feiertage berücksichtigt. 
                Zeiträume davor bleiben unverändert, damit bereits genehmigte und abgerechnete Zeiten nicht nachträglich abweichen.
              </p>
            </div>

            {/* Warning for past date */}
            {showHolidayWarning && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-600 dark:text-amber-400 font-medium">Achtung: Rückwirkendes Datum</p>
                    <p className="text-amber-600 dark:text-amber-400/80 text-sm mt-1">
                      Sollstunden und Zuschläge werden für den Zeitraum ab diesem Datum neu berechnet. 
                      Bereits genehmigte Wochen können dadurch von den ursprünglich berechneten Werten abweichen.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Current Settings Info */}
            {holidaySettings && (
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-muted-foreground">Aktuelle Einstellungen</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Bundesland: <span className="text-foreground">{getStateName(holidaySettings.default_state)}</span>
                      {holidaySettings.holiday_calculation_start_date && (
                        <span>
                          {' '} · Gültig ab: <span className="text-foreground">
                            {safeFormatDate(holidaySettings.holiday_calculation_start_date, 'd. MMMM yyyy')}
                          </span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                onClick={handleHolidaySettingsSave}
                disabled={isHolidaySaving}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                {isHolidaySaving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Einstellungen speichern
              </Button>
            </div>
          </div>

          {/* Link to Holidays Page */}
          <div className="card-glass p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-foreground font-medium">Feiertagsübersicht</p>
                <p className="text-sm text-muted-foreground">Alle Feiertage anzeigen und benutzerdefinierte Feiertage verwalten</p>
              </div>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/holidays'}
                className="border-border text-muted-foreground hover:text-foreground"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Zur Übersicht
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Payroll Rules Tab */}
        <TabsContent value="payroll" className="mt-6">
          <PayrollRulesTab />
        </TabsContent>

        {/* Attributes Tab */}
        <TabsContent value="attributes" className="mt-6 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Benutzerdefinierte Felder für Mitarbeiterprofile</p>
            <Button onClick={openAttrDialog} className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
              <Plus className="w-4 h-4 mr-2" />
              Neues Feld
            </Button>
          </div>

          <div className="card-glass overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Feldname</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Anzeigename</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Typ</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Pflicht</th>
                  <th className="text-right p-4 text-xs font-medium text-muted-foreground uppercase">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {userAttributes.map((attr) => (
                  <tr key={attr.name} className="hover:bg-accent/20">
                    <td className="p-4 text-foreground font-mono text-sm">{attr.name}</td>
                    <td className="p-4 text-muted-foreground">{attr.display_name}</td>
                    <td className="p-4 text-muted-foreground">{attr.field_type}</td>
                    <td className="p-4">
                      {attr.required ? (
                        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground" />
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {!attr.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleDeleteAttr(attr.name)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {attr.is_default && (
                        <span className="text-xs text-muted-foreground">Standard</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* DATEV Tab */}
        <TabsContent value="datev" className="mt-6 space-y-4">
          <p className="text-muted-foreground">DATEV-Lohnarten für die Arbeitszeiterfassung</p>

          <div className="card-glass overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Code</th>
                  <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase">Bezeichnung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {wageTypes.map((type) => (
                  <tr key={type.code} className="hover:bg-accent/20">
                    <td className="p-4 text-foreground font-mono">{type.code}</td>
                    <td className="p-4 text-muted-foreground">{type.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Invoice Tab */}
        <TabsContent value="invoice" className="mt-6 space-y-6">
          {/* A) Firmenstammdaten */}
          <div className="card-glass p-6 space-y-6">
            <div>
              <h2 className="text-lg font-medium text-foreground">Firmenstammdaten</h2>
              <p className="text-sm text-muted-foreground mt-1">Diese Angaben erscheinen im Kopf und in der Fußzeile jeder Rechnung.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Firma */}
              <p className="text-sm font-medium text-muted-foreground col-span-full">Firma</p>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Firmenname</Label>
                <Input
                  value={companyProfileForm.legal_name || ''}
                  onChange={(e) => handleCompanyProfileChange('legal_name', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Adresse</Label>
                <Input
                  value={companyProfileForm.address_line1 || ''}
                  onChange={(e) => handleCompanyProfileChange('address_line1', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">PLZ</Label>
                <Input
                  value={companyProfileForm.postal_code || ''}
                  onChange={(e) => handleCompanyProfileChange('postal_code', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Ort</Label>
                <Input
                  value={companyProfileForm.city || ''}
                  onChange={(e) => handleCompanyProfileChange('city', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Adresszusatz</Label>
                <Input
                  value={companyProfileForm.address_line2 || ''}
                  onChange={(e) => handleCompanyProfileChange('address_line2', e.target.value)}
                  className="bg-input border-border text-foreground"
                  placeholder="optional"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Land (E-Rechnung BT-40)</Label>
                <CountrySelect
                  value={companyProfileForm.country_code || 'DE'}
                  onChange={(code) => { handleCompanyProfileChange('country_code', code); handleCompanyProfileChange('country', countryName(code)); }}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Handelsname, falls abweichend (BT-28)</Label>
                <Input
                  value={companyProfileForm.trading_name || ''}
                  onChange={(e) => handleCompanyProfileChange('trading_name', e.target.value)}
                  className="bg-input border-border text-foreground"
                  placeholder="optional"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Kontaktstelle für Rechnungen (BT-41)</Label>
                <Input
                  value={companyProfileForm.contact_name || ''}
                  onChange={(e) => handleCompanyProfileChange('contact_name', e.target.value)}
                  className="bg-input border-border text-foreground"
                  placeholder="z. B. Buchhaltung"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Telefon</Label>
                <Input
                  value={companyProfileForm.phone || ''}
                  onChange={(e) => handleCompanyProfileChange('phone', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">E-Mail</Label>
                <Input
                  type="email"
                  value={companyProfileForm.email || ''}
                  onChange={(e) => handleCompanyProfileChange('email', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Website</Label>
                <Input
                  value={companyProfileForm.website || ''}
                  onChange={(e) => handleCompanyProfileChange('website', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              
              {/* Niederlassung */}
              <p className="text-sm font-medium text-muted-foreground col-span-full mt-4">Niederlassung</p>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Niederlassungsname</Label>
                <Input
                  value={companyProfileForm.branch_name || ''}
                  onChange={(e) => handleCompanyProfileChange('branch_name', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Adresse</Label>
                <Input
                  value={companyProfileForm.branch_address_line1 || ''}
                  onChange={(e) => handleCompanyProfileChange('branch_address_line1', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">PLZ</Label>
                <Input
                  value={companyProfileForm.branch_postal_code || ''}
                  onChange={(e) => handleCompanyProfileChange('branch_postal_code', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Ort</Label>
                <Input
                  value={companyProfileForm.branch_city || ''}
                  onChange={(e) => handleCompanyProfileChange('branch_city', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Telefon</Label>
                <Input
                  value={companyProfileForm.branch_phone || ''}
                  onChange={(e) => handleCompanyProfileChange('branch_phone', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              
              {/* Register und Steuern */}
              <p className="text-sm font-medium text-muted-foreground col-span-full mt-4">Register und Steuern</p>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Registergericht</Label>
                <Input
                  value={companyProfileForm.register_court || ''}
                  onChange={(e) => handleCompanyProfileChange('register_court', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Registernummer</Label>
                <Input
                  value={companyProfileForm.register_number || ''}
                  onChange={(e) => handleCompanyProfileChange('register_number', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Geschäftsführer</Label>
                <Input
                  value={companyProfileForm.managing_director || ''}
                  onChange={(e) => handleCompanyProfileChange('managing_director', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Steuernummer</Label>
                <Input
                  value={companyProfileForm.tax_number || ''}
                  onChange={(e) => handleCompanyProfileChange('tax_number', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">USt-IdNr.</Label>
                <Input
                  value={companyProfileForm.vat_id || ''}
                  onChange={(e) => handleCompanyProfileChange('vat_id', e.target.value)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              
              {/* Bank: seit X2 als eigene Liste „Bankkonten" weiter unten */}
              <p className="text-sm text-muted-foreground col-span-full mt-4">
                Bankverbindungen werden unter „Bankkonten“ verwaltet (unterhalb dieses Abschnitts).
              </p>
            </div>
            
            {/* Logo */}
            <div className="pt-4 border-t border-border space-y-4">
              <p className="text-sm font-medium text-muted-foreground">Logo</p>
              {companyProfileForm.logo_base64 && (
                <div className="flex items-center gap-4">
                  <img
                    src={companyProfileForm.logo_base64}
                    alt="Firmenlogo"
                    className="max-h-20 object-contain"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleRemoveLogo}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Logo entfernen
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="logo-upload" className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-input hover:bg-accent/50 text-foreground text-sm">
                  <Upload className="w-4 h-4" />
                  Logo auswählen
                </Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <p className="text-xs text-muted-foreground">Empfohlen: Breite mindestens 1500 Pixel, Seitenverhältnis wie der bisherige Briefkopf.</p>
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="button"
                onClick={handleSaveCompanyProfile}
                disabled={!companyProfileChanged || isSavingCompanyProfile}
                className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
              >
                {isSavingCompanyProfile ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          </div>
          
          {/* A2) Bankkonten (X2) */}
          <BankAccountsPanel api={api} canManage={user?.role === 'admin'} />

          {/* B) Rechnungsoptionen */}
          <div className="card-glass p-6 space-y-6">
            <div>
              <h2 className="text-lg font-medium text-foreground">Rechnungsoptionen</h2>
              <p className="text-sm text-muted-foreground mt-1">Standardwerte für alle Rechnungen. Einzelne Kunden können davon abweichen.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Zahlungsziel (Tage)</Label>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  value={invoiceDefaultsForm.payment_term_days ?? 14}
                  onChange={(e) => handleInvoiceDefaultsChange('payment_term_days', parseInt(e.target.value) || 0)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Detailgrad auf der Rechnung</Label>
                <Select
                  value={invoiceDefaultsForm.detail_level || 'none'}
                  onValueChange={(v) => handleInvoiceDefaultsChange('detail_level', v)}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none">Nur Positionen</SelectItem>
                    <SelectItem value="weeks">Einzelnachweis je Kalenderwoche</SelectItem>
                    <SelectItem value="days">Einzelnachweis je Tag</SelectItem>
                    <SelectItem value="entries">Einzelnachweis je Buchung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Aufteilung der Betreuungsstunden</Label>
                <Select
                  value={invoiceDefaultsForm.position_split || 'combined'}
                  onValueChange={(v) => handleInvoiceDefaultsChange('position_split', v)}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="combined">Eine Position, Tätigkeiten im Text</SelectItem>
                    <SelectItem value="per_activity">Eine Position je Tätigkeit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Umsatzsteuersatz (%)</Label>
                <Input
                  type="number"
                  step={0.1}
                  min={0}
                  max={100}
                  value={invoiceDefaultsForm.default_vat_rate ?? 19}
                  onChange={(e) => handleInvoiceDefaultsChange('default_vat_rate', parseFloat(e.target.value) || 0)}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Kilometersatz (€)</Label>
                <Input
                  type="number"
                  step={0.01}
                  min={0}
                  max={10}
                  value={invoiceDefaultsForm.mileage_rate_per_km ?? 0.30}
                  onChange={(e) => handleInvoiceDefaultsChange('mileage_rate_per_km', parseFloat(e.target.value) || 0)}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium">Bestellnummer erforderlich</h3>
                  <p className="text-sm text-muted-foreground">Rechnungen ohne Bestellnummer können dann nicht erzeugt werden.</p>
                </div>
                <Switch
                  checked={invoiceDefaultsForm.require_po_number ?? false}
                  onCheckedChange={(checked) => handleInvoiceDefaultsChange('require_po_number', checked)}
                />
              </div>
              
              <div className="border-t border-border" />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium">Zuschläge als eigene Positionen</h3>
                  <p className="text-sm text-muted-foreground">Andernfalls werden Zuschläge in den Positionsbetrag eingerechnet.</p>
                </div>
                <Switch
                  checked={invoiceDefaultsForm.surcharges_as_positions ?? true}
                  onCheckedChange={(checked) => handleInvoiceDefaultsChange('surcharges_as_positions', checked)}
                />
              </div>
              
              <div className="border-t border-border" />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium">Positionen mit 0,00 € ausblenden</h3>
                </div>
                <Switch
                  checked={invoiceDefaultsForm.hide_zero_positions ?? false}
                  onCheckedChange={(checked) => handleInvoiceDefaultsChange('hide_zero_positions', checked)}
                />
              </div>
              
              <div className="border-t border-border" />
              
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium">Ansprechpartner des Kunden anzeigen</h3>
                </div>
                <Switch
                  checked={invoiceDefaultsForm.show_contact_person ?? true}
                  onCheckedChange={(checked) => handleInvoiceDefaultsChange('show_contact_person', checked)}
                />
              </div>
            </div>
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="button"
                onClick={handleSaveInvoiceDefaults}
                disabled={!invoiceDefaultsChanged || isSavingInvoiceDefaults}
                className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
              >
                {isSavingInvoiceDefaults ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          </div>
          
          {/* C) Rechnungsnummern */}
          <div className="card-glass p-6 space-y-6">
            <div>
              <h2 className="text-lg font-medium text-foreground">Rechnungsnummern</h2>
            </div>
            
            {/* Test Mode Warning */}
            {!invoiceNumbering.live_mode && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-500 font-medium">Testbetrieb</p>
                    <p className="text-amber-500/80 text-sm mt-1">
                      Ausgestellte Rechnungen erhalten Testnummern (TEST-JJJJ-NNNN) und tragen ein Wasserzeichen. Der produktive Nummernkreis wird nicht verbraucht.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Live Mode Info */}
            {invoiceNumbering.live_mode && (
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-emerald-500 font-medium">Livebetrieb aktiv seit {invoiceNumbering.live_since ? safeFormatDate(invoiceNumbering.live_since, 'd. MMMM yyyy') : '–'}</p>
                    <p className="text-emerald-500/80 text-sm mt-1">
                      Es werden fortlaufende Rechnungsnummern vergeben.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Fields */}
            {!invoiceNumbering.live_mode ? (
              // Test Mode - Editable
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Format</Label>
                    <Input
                      value={invoiceNumberingForm.format || '{seq}/{year}'}
                      onChange={(e) => handleInvoiceNumberingChange('format', e.target.value)}
                      className="bg-input border-border text-foreground"
                    />
                    <p className="text-xs text-muted-foreground">Platzhalter: {'{seq}'} laufende Nummer, {'{year}'} Jahr vierstellig, {'{yy}'} zweistellig, {'{prefix}'} Präfix</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Präfix</Label>
                    <Input
                      value={invoiceNumberingForm.prefix || ''}
                      onChange={(e) => handleInvoiceNumberingChange('prefix', e.target.value)}
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Führende Nullen</Label>
                    <Input
                      type="number"
                      min={0}
                      max={8}
                      value={invoiceNumberingForm.seq_padding ?? 0}
                      onChange={(e) => handleInvoiceNumberingChange('seq_padding', parseInt(e.target.value) || 0)}
                      className="bg-input border-border text-foreground"
                    />
                    <p className="text-xs text-muted-foreground">0 = keine führenden Nullen</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Nächste Rechnungsnummer</Label>
                    <Input
                      type="number"
                      min={1}
                      value={invoiceNumberingForm.next_seq ?? 1}
                      onChange={(e) => handleInvoiceNumberingChange('next_seq', parseInt(e.target.value) || 1)}
                      className="bg-input border-border text-foreground"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h3 className="text-foreground font-medium">Zähler jährlich zurücksetzen</h3>
                  </div>
                  <Switch
                    checked={invoiceNumberingForm.reset_yearly ?? false}
                    onCheckedChange={(checked) => handleInvoiceNumberingChange('reset_yearly', checked)}
                  />
                </div>
                
                {/* Preview */}
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-sm text-muted-foreground">
                    Nächste Rechnung im Livebetrieb: <span className="text-foreground font-medium">{formatInvoiceNumberPreview()}</span>
                  </p>
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                  <Button
                    type="button"
                    onClick={handleSaveInvoiceNumbering}
                    disabled={!invoiceNumberingChanged || isSavingInvoiceNumbering}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
                  >
                    {isSavingInvoiceNumbering ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Speichern
                  </Button>
                </div>
                
                {/* Live Mode Activation */}
                <div className="p-4 rounded-lg border-2 border-red-500/30 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Wenn Sie bereit sind, echte Rechnungsnummern zu vergeben, können Sie den Livebetrieb starten.
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowLiveModeDialog(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Livebetrieb starten
                  </Button>
                </div>
              </div>
            ) : (
              // Live Mode - Read-only (except next_seq)
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Format</Label>
                    <p className="text-foreground font-mono p-2 bg-muted/30 rounded border border-border">{invoiceNumbering.format || '{seq}/{year}'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Präfix</Label>
                    <p className="text-foreground font-mono p-2 bg-muted/30 rounded border border-border">{invoiceNumbering.prefix || '–'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Führende Nullen</Label>
                    <p className="text-foreground font-mono p-2 bg-muted/30 rounded border border-border">{invoiceNumbering.seq_padding ?? 0}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Zähler jährlich zurücksetzen</Label>
                    <p className="text-foreground p-2 bg-muted/30 rounded border border-border">{invoiceNumbering.reset_yearly ? 'Ja' : 'Nein'}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Nächste Rechnungsnummer</Label>
                  <Input
                    type="number"
                    min={1}
                    value={invoiceNumberingForm.next_seq ?? 1}
                    onChange={(e) => handleInvoiceNumberingChange('next_seq', parseInt(e.target.value) || 1)}
                    className="bg-input border-border text-foreground max-w-xs"
                  />
                  {!canSaveNextSeqInLiveMode() && (
                    <p className="text-xs text-red-400">Die Nummer kann nur erhöht werden.</p>
                  )}
                </div>
                
                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                  <Button
                    type="button"
                    onClick={handleSaveInvoiceNumbering}
                    disabled={!invoiceNumberingChanged || isSavingInvoiceNumbering || !canSaveNextSeqInLiveMode()}
                    className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
                  >
                    {isSavingInvoiceNumbering ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Speichern
                  </Button>
                </div>
              </div>
            )}
            
            {/* Test numbers info */}
            {invoiceNumbering.test_seq !== undefined && (
              <p className="text-sm text-muted-foreground">
                Bisher vergebene Testnummern: {invoiceNumbering.test_seq}
              </p>
            )}
          </div>
          
          {/* D) Berechtigungen für die Rechnungsstellung */}
          <div className="card-glass p-6 space-y-6">
            <div>
              <h2 className="text-lg font-medium text-foreground">Berechtigungen für die Rechnungsstellung</h2>
              <p className="text-sm text-muted-foreground mt-1">Legt fest, welche Rolle welche Schritte ausführen darf. Administratoren dürfen immer alles.</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 pr-4 text-muted-foreground font-medium">Aktion</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-medium whitespace-nowrap">Buchhaltung</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-medium whitespace-nowrap">Vorgesetzte</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-medium whitespace-nowrap">Administration</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { action: 'create', title: 'Rechnung erstellen', desc: 'Entwurf aus ausgewählten Zeiteinträgen erzeugen' },
                    { action: 'edit', title: 'Rechnung bearbeiten', desc: 'Positionstexte und manuelle Positionen ändern (nur im Entwurf)' },
                    { action: 'approve', title: 'Rechnung freigeben', desc: 'Entwurf inhaltlich freigeben und Freigabe zurückziehen' },
                    { action: 'issue', title: 'Rechnung ausstellen', desc: 'Rechnungsnummer vergeben und PDF unveränderlich speichern' },
                    { action: 'send', title: 'Als versendet markieren', desc: 'Kennzeichnen, dass die Rechnung beim Kunden ist' },
                    { action: 'void', title: 'Rechnung stornieren', desc: 'Stornorechnung mit eigener Nummer erzeugen' },
                    { action: 'delete', title: 'Rechnung löschen', desc: 'Entwürfe und freigegebene Rechnungen löschen, Zeiteinträge werden freigegeben' },
                  ].map(({ action, title, desc }) => {
                    const roles = billingPermissionsForm[action] || [];
                    const noRolesSelected = roles.length === 0;
                    return (
                      <tr key={action} className="border-b border-border/50">
                        <td className="py-3 pr-4">
                          <div className="font-medium text-foreground">{title}</div>
                          <div className="text-xs text-muted-foreground">{desc}</div>
                          {noRolesSelected && (
                            <div className="mt-2 text-xs text-amber-500 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Nur die Administration kann diesen Schritt ausführen.
                            </div>
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          <Checkbox
                            checked={roles.includes('accounting')}
                            onCheckedChange={() => handleBillingPermissionToggle(action, 'accounting')}
                            data-testid={`billing-perm-${action}-accounting`}
                          />
                        </td>
                        <td className="text-center py-3 px-4">
                          <Checkbox
                            checked={roles.includes('supervisor')}
                            onCheckedChange={() => handleBillingPermissionToggle(action, 'supervisor')}
                            data-testid={`billing-perm-${action}-supervisor`}
                          />
                        </td>
                        <td className="text-center py-3 px-4">
                          <div className="relative inline-block group">
                            <Checkbox
                              checked={true}
                              disabled={true}
                              className="opacity-70"
                              data-testid={`billing-perm-${action}-admin`}
                            />
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs bg-card border border-border rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              Administratoren sind immer berechtigt.
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Four-eyes warning */}
            {hasFourEyesWarning() && (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-500 text-sm">
                      Freigabe und Ausstellung liegen bei derselben Rolle. Das Vier-Augen-Prinzip ist damit aufgehoben.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="button"
                onClick={handleSaveBillingPermissions}
                disabled={!billingPermissionsChanged || isSavingBillingPermissions}
                className="bg-indigo-500 hover:bg-indigo-600 text-white disabled:opacity-50"
                data-testid="save-billing-permissions-btn"
              >
                {isSavingBillingPermissions ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Speichern
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Display Settings Tab */}
        <TabsContent value="display" className="mt-6 space-y-4">
          <p className="text-muted-foreground">Anzeigeeinstellungen für verschiedene Benutzerrollen</p>

          <div className="card-glass p-6 space-y-6">
            {/* Utilization visibility */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-400" />
                    Projekt-Ausschöpfung für Mitarbeiter
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-lg">
                    Wenn aktiviert, sehen Mitarbeiter den Ausschöpfungsgrad von Projekten in Prozent.
                    Absolute Stunden, Budgets und Sätze bleiben für Mitarbeiter immer verborgen.
                  </p>
                </div>
                <Switch
                  checked={employeesSeePct}
                  onCheckedChange={handleUtilizationVisibilityChange}
                  disabled={isSavingPctSetting}
                />
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-border" />

            {/* Pausenzeiten-Pflicht (§ 4 ArbZG) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-400" />
                    Pausenzeiten mit Beginn und Ende Pflicht
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-lg">
                    Wenn aktiviert, lässt sich eine Arbeitszeit über 6 Stunden nur speichern, wenn die Pausen
                    mit Beginn und Ende eingetragen sind (§ 4 ArbZG). Gestempelte Pausen werden automatisch erfasst.
                  </p>
                </div>
                <Switch
                  checked={requireBreakTimes}
                  onCheckedChange={handleBreakTimesChange}
                  disabled={isSavingBreakTimes}
                />
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-border" />            
            
            {/* Supervisor Scope */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-400" />
                    Reichweite für Vorgesetzte
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-lg">
                    Legt fest, ob Vorgesetzte in Team-Übersicht, fehlenden Wocheneinreichungen und 
                    Arbeitszeitkonten nur ihr eigenes Team oder alle Mitarbeitenden sehen.
                  </p>
                </div>
                <Select
                  value={supervisorScope}
                  onValueChange={(value) => handleTeamOverviewSettingsChange(value, accountingSeesTeamOverview)}
                  disabled={isSavingTeamOverviewSettings}
                >
                  <SelectTrigger className="w-[180px] bg-input border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="team">Eigenes Team</SelectItem>
                    <SelectItem value="company">Gesamte Firma</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-border" />
            
            {/* Accounting Team Overview */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-400" />
                    Team-Übersicht für Buchhaltung
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-lg">
                    Wenn aktiviert, sieht die Buchhaltung die Team-Übersicht im Menü.
                  </p>
                </div>
                <Switch
                  checked={accountingSeesTeamOverview}
                  onCheckedChange={(checked) => handleTeamOverviewSettingsChange(supervisorScope, checked)}
                  disabled={isSavingTeamOverviewSettings}
                />
              </div>
            </div>
            
            {/* Divider */}
            <div className="border-t border-border" />
            
            {/* Billing Adjustment Permission */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-foreground font-medium flex items-center gap-2">
                    <Eye className="w-4 h-4 text-violet-400" />
                    Abrechnungsmengen durch Buchhaltung
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-lg">
                    Wenn aktiviert, darf die Buchhaltung die abgerechnete Menge einzelner Zeiteinträge selbst anpassen. Andernfalls erfolgt die Anpassung durch Vorgesetzte oder Geschäftsführung.
                  </p>
                </div>
                <Switch
                  checked={accountingMayAdjustBilling}
                  onCheckedChange={handleBillingAdjustmentChange}
                  disabled={isSavingBillingAdjustment}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Clock Tab - Admin only */}
        {user?.role === 'admin' && (
          <TabsContent value="clock" className="mt-6 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">Automatisch beendete Stempelsitzungen (Ausstempeln vergessen, Session &gt; 12h offen)</p>
              <Button 
                type="button"
                onClick={fetchAutoClosedSessions} 
                variant="outline" 
                size="sm" 
                className="border-border text-muted-foreground"
              >
                Aktualisieren
              </Button>
            </div>
            
            {autoClosedLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 rounded-lg bg-muted/30 animate-pulse" />
                ))}
              </div>
            ) : autoClosedSessions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Keine automatisch beendeten Sitzungen.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Name</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Datum</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Beginn</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Auto beendet am</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Eintrag</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Hinweis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoClosedSessions.map((session, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="p-3 text-foreground">
                          {session.first_name} {session.last_name}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {session.date ? safeFormatDate(session.date) : '-'}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {session.clock_in ? new Date(session.clock_in).toLocaleTimeString('de-DE', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            timeZone: 'Europe/Berlin'
                          }) : '-'}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {session.auto_closed_at ? new Date(session.auto_closed_at).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'Europe/Berlin'
                          }) : '-'}
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            session.has_work_time_entry 
                              ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' 
                              : 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                          }`}>
                            {session.has_work_time_entry ? 'Ja' : 'Nein'}
                          </span>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-xs truncate" title={session.auto_close_note || ''}>
                          {session.auto_close_note || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Role Dialog */}
      <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {editingRole ? 'Rolle bearbeiten' : 'Neue Rolle'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleRoleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Technischer Name</Label>
                <Input
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  disabled={!!editingRole}
                  required
                  placeholder="projekt_leiter"
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Anzeigename</Label>
                <Input
                  value={roleForm.display_name}
                  onChange={(e) => setRoleForm({ ...roleForm, display_name: e.target.value })}
                  required
                  placeholder="Projektleiter"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Beschreibung</Label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Kurze Beschreibung der Rolle..."
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Berechtigungen</Label>
              <div className="grid grid-cols-2 gap-2 p-4 rounded-lg bg-muted/50">
                {permissions.map((perm) => (
                  <label key={perm.name} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={roleForm.permissions.includes(perm.name)}
                      onCheckedChange={() => togglePermission(perm.name)}
                    />
                    <span className="text-sm text-muted-foreground">{perm.display_name}</span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRoleDialogOpen(false)} className="border-border text-muted-foreground">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                {editingRole ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Attribute Dialog */}
      <Dialog open={isAttrDialogOpen} onOpenChange={setIsAttrDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Neues Benutzerfeld</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleAttrSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Technischer Name</Label>
                <Input
                  value={attrForm.name}
                  onChange={(e) => setAttrForm({ ...attrForm, name: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  required
                  placeholder="vertragsart"
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Anzeigename</Label>
                <Input
                  value={attrForm.display_name}
                  onChange={(e) => setAttrForm({ ...attrForm, display_name: e.target.value })}
                  required
                  placeholder="Vertragsart"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Feldtyp</Label>
                <Select value={attrForm.field_type} onValueChange={(v) => setAttrForm({ ...attrForm, field_type: v })}>
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="number">Zahl</SelectItem>
                    <SelectItem value="date">Datum</SelectItem>
                    <SelectItem value="select">Auswahl</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 flex items-end">
                <label className="flex items-center gap-2 cursor-pointer p-2">
                  <Checkbox
                    checked={attrForm.required}
                    onCheckedChange={(c) => setAttrForm({ ...attrForm, required: c })}
                  />
                  <span className="text-sm text-muted-foreground">Pflichtfeld</span>
                </label>
              </div>
            </div>

            {attrForm.field_type === 'select' && (
              <div className="space-y-2">
                <Label className="text-muted-foreground">Optionen (kommagetrennt)</Label>
                <Input
                  value={attrForm.options}
                  onChange={(e) => setAttrForm({ ...attrForm, options: e.target.value })}
                  placeholder="Vollzeit, Teilzeit, Minijob"
                  className="bg-input border-border text-foreground"
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsAttrDialogOpen(false)} className="border-border text-muted-foreground">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600 text-foreground">
                Erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Live Mode Confirmation Dialog */}
      <Dialog open={showLiveModeDialog} onOpenChange={setShowLiveModeDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Livebetrieb starten
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-muted-foreground">
              Ab sofort werden echte Rechnungsnummern vergeben. Format und Startnummer können danach nicht mehr geändert werden, die Nummer kann nur noch erhöht werden. Dieser Schritt kann nicht rückgängig gemacht werden.
            </p>
            
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm text-muted-foreground">
                Erste Rechnungsnummer: <span className="text-foreground font-medium">{formatInvoiceNumberPreview()}</span>
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Geben Sie zur Bestätigung <span className="font-bold text-foreground">LIVE</span> ein:</Label>
              <Input
                value={liveModeConfirmText}
                onChange={(e) => setLiveModeConfirmText(e.target.value)}
                placeholder="LIVE"
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowLiveModeDialog(false);
                setLiveModeConfirmText('');
              }}
              className="border-border text-muted-foreground"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleStartLiveMode}
              disabled={liveModeConfirmText !== 'LIVE' || isSavingInvoiceNumbering}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {isSavingInvoiceNumbering ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : null}
              Livebetrieb starten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
